import { Box, Stack, Typography } from '@mui/material';
import { PmStepNavigator, type PmStep } from '../../../../components/pm';

interface DashboardStepIndicatorsProps {
  steps: PmStep[];
  onSelect: (step: PmStep) => void;
}

/** Phasen-Grenzen analog Sidebar-/Dashboard-Gating: Init 1–7, Plan 8–13, Exec 14–17. */
const PHASES: Array<{ key: string; label: string; from: number; to: number }> = [
  { key: 'init', label: 'Initiierung', from: 1, to: 7 },
  { key: 'plan', label: 'Planung', from: 8, to: 13 },
  { key: 'exec', label: 'Ausführung', from: 14, to: 17 },
];

/** Status-Ampel — DEV-Semantik (Reifegrad-Status), im Clickdummy-Stil dargestellt. */
const STATUS_LEGEND: Array<{ color: string; label: string }> = [
  { color: 'var(--pm-error)', label: 'offen — Handlungsbedarf' },
  { color: 'var(--pm-warning)', label: 'in Arbeit' },
  { color: 'var(--pm-success)', label: 'läuft / erledigt' },
  { color: 'var(--pm-gray-300)', label: 'Phase noch nicht erreicht' },
];

/** Reifegrad-Skala schwach (dunkel) → kräftig (hell). */
const PETROL_SCALE = [
  'var(--pm-bg-deep)',
  'var(--pm-petrol-100)',
  'var(--pm-petrol-250)',
  'var(--pm-petrol-400)',
  'var(--pm-petrol-600)',
  'var(--pm-petrol-800)',
];

/**
 * Schritt-Indikatoren nach 17-Schritte-Framework — drei Phasen-Gruppen mit
 * Ampel-Legende oben und Reifegrad-Skala-Legende unten (Clickdummy-Dashboard).
 */
export function DashboardStepIndicators({ steps, onSelect }: DashboardStepIndicatorsProps) {
  return (
    <Stack spacing={1.5}>
      {/* Ampel-Legende */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {STATUS_LEGEND.map((l) => (
          <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: l.color, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', fontSize: '0.7rem' }}>
              {l.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Phasen-Gruppen */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
        }}
      >
        {PHASES.map((ph, i) => {
          const phSteps = steps.filter((s) => s.number >= ph.from && s.number <= ph.to);
          return (
            <Box
              key={ph.key}
              sx={{ display: 'flex', alignItems: 'stretch', gap: 1, flex: '1 1 auto', minWidth: 0 }}
            >
              <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'var(--pm-fg-subtle)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontWeight: 600,
                    pl: 0.5,
                  }}
                >
                  {ph.label}
                </Typography>
                <PmStepNavigator steps={phSteps} onSelect={onSelect} />
              </Box>
              {i < PHASES.length - 1 && (
                <Box
                  sx={{
                    width: '1px',
                    alignSelf: 'stretch',
                    background: 'var(--pm-bg-deep)',
                    mx: 0.5,
                    display: { xs: 'none', md: 'block' },
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Reifegrad-Skala-Legende */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
          Reifegrad pro Schritt:
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
          schwach
        </Typography>
        <Box
          sx={{
            display: 'flex',
            height: 10,
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid var(--pm-bg-deep)',
          }}
        >
          {PETROL_SCALE.map((c, i) => (
            <Box key={i} sx={{ width: 18, background: c }} />
          ))}
        </Box>
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-base)', fontWeight: 500 }}>
          kräftig
        </Typography>
        <Typography
          variant="caption"
          sx={{ ml: 'auto', fontStyle: 'italic', color: 'var(--pm-fg-subtle)' }}
        >
          Tipp: Klick auf einen Schritt-Kreis öffnet das Modul
        </Typography>
      </Box>
    </Stack>
  );
}
